<template>
  <RevPrintWrapper class="qa-print">
    <div slot="header" class="qa-print__header ">
      <PrintHeader
        v-if="rev.name"
        logoUrlWidth="120"
        revFontSize="12"
        :revName="rev.name"
        :siteName="rev.siteName"
        :tags="rev.tags"
        :completedDate="rev.completedDate"
        :creationDate="rev.creationDate"
        :logoMediaObject="logoMediaObject"
        :defaultLogo="DEFAULT_LOGO"
        :rev="rev"
        :statusText="rev.statusText"
        :validationDate="rev.validationDate"
        :isValidated="rev.isValidated"
        :isFinanciallyValidated="rev.isFinanciallyValidated"
        :isHighlighted="rev.isHighlighted"
        :challengeName="challengeName"
      />
    </div>
    <div v-if="owner.name" slot="info" class="qa-print__content">
      <Card
        :title="$t('common.roles.collaborator_other')"
        data-testid="team_card"
        class="qa-print__content-team mb-10"
      >
        <template slot="content">
          <Team :owner="owner" :coach="coach" :members="collaborators" />
        </template>
      </Card>
      <div class="qa-print__content-cycle mt-1">
        <Phases
          class="qa-print__content-phases mb-8"
          :class="{ fullHeight: !hasReviewInfo }"
          :before="beforePhase"
          :after="afterPhase"
        />
        <ImpactsPrintView :rev="rev"></ImpactsPrintView>
      </div>
      <div v-if="isTranslated" class="d-flex align-end justify-end">
        {{ $t('rev.content_translated') }}
      </div>
    </div>
  </RevPrintWrapper>
</template>
<script>
import { mapState, mapActions } from 'vuex'
import _ from 'lodash'
import mixin from '@/mixins/mixin'
import { utils } from 'frontend-js-shared'
import userMixin from '@/mixins/userMixin'
import RevPrintWrapper from '@/components/RevPrint/RevPrintWrapper.vue'
import PrintHeader from '@/components/RevPrint/PrintHeader.vue'
import Team from './Team.vue'
import Phases from './Phases.vue'
import Card from '@/components/RevPrint/Card'
import DEFAULT_LOGO from '@/assets/images/logo-rever.svg'
import ImpactsPrintView from '@/components/Rev/Review/ImpactsPrintView.vue'

export default {
  name: 'QuickActionPrintView',

  mixins: [mixin, userMixin],

  components: {
    RevPrintWrapper,
    PrintHeader,
    Team,
    Phases,
    Card,
    ImpactsPrintView,
  },

  props: {
    organizationId: {
      type: String,
      required: true,
    },
    revId: {
      type: String,
      required: true,
    },
    isTranslated: {
      required: false,
      type: Boolean,
    },
  },

  data() {
    return {
      DEFAULT_LOGO,
      attributes: [],
      situationTypes: '',
    }
  },

  async mounted() {
    if (this.isTranslated) {
      await this.translateRev()
    } else {
      await this.getRev({
        organizationId: this.organizationId,
        revId: this.revId,
      })
    }

    this.situationTypes = await this.getSituationTypes()
    await this.getAttributesOptions()
    await this.getRevChallenge({
      organizationId: this.organizationId,
      revId: this.revId,
    })
  },

  computed: {
    ...mapState({
      logoMediaObject: state => state.access.logoMediaObject,
    }),

    ...mapState('quickActionPrint', {
      challenge: state => state.challenge,
      rev({ rev }) {
        return {
          name: rev?.name,
          siteName: rev?.location?.site?.name,
          tags: rev?.location?.tags ?? [],
          challengeName: rev?.challenge?.name ?? '',
          formattedStatus: _.snakeCase(rev.status),
          completedDate: this.getCompletedDate(rev),
          creationDate: this.getCreationDate(rev),
          isValidated: Boolean(rev?.hasImplementationValidation),
          isFinanciallyValidated: Boolean(rev?.hasFinancialValidation),
          isHighlighted: Boolean(rev?.isHighlighted),
          statusText: this.getStatusText(rev),
          validationDate: this.getValidationDate(rev),
          ...rev,
        }
      },
    }),

    challengeName() {
      return this.challenge?.name ?? ''
    },

    owner() {
      const owner = this.rev?.team?.owner
      return owner ? this.formatUser(owner) : {}
    },

    coach() {
      const coach = this.rev?.team?.coach
      return coach ? this.formatUser(coach) : {}
    },

    collaborators() {
      if (!this.rev?.team?.collaborators) return []
      return this.rev.team.collaborators.map(collaborator =>
        this.formatUser(collaborator)
      )
    },

    afterPhase() {
      return {
        media: this.rev.afterPhase?.media[0],
        solution: this.rev.afterPhase?.solution,
        suggestedTags: this.getSuggestedTags(),
      }
    },

    beforePhase() {
      return {
        media: this.rev.beforePhase?.media[0],
        situation: this.rev.beforePhase?.situation,
        situationTypes: this.situationTypes,
      }
    },

    costReduction() {
      return (
        parseInt(this.rev?.reviewPhase?.costReduction?.capturedValue) || null
      )
    },

    investment() {
      return parseInt(this.rev?.reviewPhase?.investment?.capturedValue) || null
    },

    revCurrency() {
      return this.rev?.currencyMetadata?.currentCurrency ?? null
    },

    revCategories() {
      return this.rev?.categories ?? null
    },

    reviewComments() {
      return this.rev?.reviewPhase?.estimationExplanation ?? null
    },

    timeInvested() {
      return parseInt(this.rev?.reviewPhase?.timeTracking?.timeInvested) || null
    },

    timeReductionByMonth() {
      return (
        parseInt(this.rev?.reviewPhase?.timeTracking?.timeReductionByMonth) ||
        null
      )
    },

    hasReviewInfo() {
      return Boolean(
        this.costReduction ||
          this.investment ||
          this.revCategories ||
          this.reviewComments ||
          this.timeInvested ||
          this.timeReductionByMonth
      )
    },
  },

  methods: {
    ...mapActions({
      getRev: 'quickActionPrint/getRev',
      getRevChallenge: 'quickActionPrint/getRevChallenge',
      getTags: 'quickActionPrint/getTags',
      translate: 'quickActionPrint/translateRev',
    }),

    async getAttributesOptions() {
      this.attributes = await this.getTags(this.rev)
      this.attributes = this.attributes.map(attribute => ({
        ...attribute,
        show: true,
        checked: this.rev.afterPhase.suggestedTags.includes(attribute._id),
      }))
    },

    getCompletedDate(rev) {
      let completedDate = _.get(rev, 'completedAt', '')
      if (completedDate) return utils.dates.formatDate(completedDate)
      return completedDate
    },

    getCreationDate(rev) {
      const rawDate = _.get(rev, 'createdAt', '')
      if (!rawDate) return ''
      return utils.dates.formatDate(rawDate)
    },

    formatUser(user) {
      return {
        name: this.getFirstWord(this.getFirstName(user)),
        lastName: this.getFirstWord(this.getLastName(user)),
        email: user.email,
        avatarMediaObject: user.photo,
        solvedRev: this.rev?.completedBy?.id === user.id,
      }
    },

    getSituationTypes() {
      const situationTypes = this.rev.beforePhase?.situationTypes ?? []
      const situations = situationTypes.map(situationType => {
        return this.$t(
          `quick_action.situation_types.situations.${situationType.situation}`
        )
      })
      return situations.join(', ')
    },

    getSuggestedTags() {
      return this.attributes
        .filter(attr => attr.checked)
        .map(tag => {
          return tag.name
        })
        .join(', ')
    },

    getStatusText(rev) {
      if (rev?.hasImplementationValidation || rev?.hasFinancialValidation) {
        return this.$t('common.rev_condition.validated')
      }

      switch (rev?.status) {
        case 'open':
          return this.$t('common.rev_condition.open')
        case 'completed':
          return this.$t('common.rev_condition.completed')
        case 'inReview':
          return this.$t('common.rev_condition.in_review')
        case 'canceled':
          return this.$t('common.rev_condition.canceled')
        case 'onHold':
          return this.$t('common.rev_condition.on_hold')
        default:
          return this.$t('common.rev_condition.in_progress')
      }
    },

    getValidationDate(rev) {
      const rawDate = rev?.validations?.implementation?.validatedAt
      if (rawDate) return utils.dates.formatDate(rawDate)
      return ''
    },

    async translateRev() {
      try {
        this.isTranslating = true

        const fieldsToTranslate = [
          'name',
          'afterPhase.solution',
          'beforePhase.situation',
          'reviewPhase.estimationExplanation',
        ]

        await this.translate({
          revId: this.revId,
          organizationId: this.organizationId,
          targetLanguage: this.$store.getters.language,
          fieldsToTranslate,
        })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isTranslating = false
      }
    },
  },
}
</script>
<style lang="scss" scoped>
@media print {
  .qa-print {
    height: 90% !important;
    page-break-after: avoid !important;
    page-break-before: avoid !important;

    &__content-team {
      min-height: 15% !important;
    }

    &-cycle {
      height: 90%;
    }
  }
}

@media screen, print {
  .fullHeight {
    height: 100% !important;
    max-height: 100% !important;
  }
  .qa-print {
    height: 96%;
    display: flex;
    flex-direction: column;
    &__content {
      display: flex;
      flex-direction: column;
      height: 100%;
      &-cycle {
        flex-grow: 1;
        height: 100%;
      }
      &-phases {
        max-height: 66%;
      }
      &-team {
        min-height: 10%;
        max-height: 12%;
      }
      &-review {
        min-height: 12%;
        max-height: 14%;
        width: 100%;
      }
    }
  }
}
</style>
